/**
 * Tenant user management — the `user-manager` surface.
 *
 * Fuller counterpart to /team (features/members/pages/TeamManagement.jsx),
 * which stays the surface every tenant owner gets. This page lists EVERY
 * profile under the tenant's keys, including soft-deleted and inactive ones so
 * a delete can be reversed.
 *
 * Editing lives on the full page at /users/:userId (double-click a row). There
 * is deliberately no inline dialog — one editing surface, one form.
 *
 * The nav entry is hidden without the role, but that is cosmetic — the real
 * gate is the same role check in server/src/routes/tenantUsers.js.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TableSortLabel from '@mui/material/TableSortLabel';
import Menu from '@mui/material/Menu';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';
import { usePageSize, useTableColumns, useTableFilters } from '../../../stores/settingsStore';

/** The product role — teacher/student/studio, from userprofiles.metadata.role. */
const ROLE_COLOR = {
  teacher: 'primary',
  student: 'info',
  owner: 'secondary',
  studio: 'warning',
};

/** Subscription buckets. Values must match applyPlan() on the server. */
const PLAN_FILTERS = [
  { value: 'paying', label: 'Paying' },
  { value: 'trial', label: 'Free trial' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'pro', label: 'Pro' },
  { value: 'standard', label: 'Standard' },
  { value: 'studio', label: 'Studio' },
  { value: 'legacy', label: 'Legacy (unconverted)' },
  { value: 'none', label: 'No plan' },
];

const TIER_LABEL = { pro: 'Pro', standard: 'Standard', studio: 'Studio', legacy: 'Legacy' };
const STATUS_LABEL = { active: 'paying', trialing: 'trial', canceled: 'canceled' };
const TIER_COLOR = { pro: 'success', standard: 'info', studio: 'warning', legacy: 'default' };

/**
 * Teacher code, from whichever side the user sits on: a teacher or studio has
 * their own code; a student carries the code(s) of the teachers they're linked
 * to. Codes resolve to actual people on the detail page.
 */
function TeacherCodeCell({ user }) {
  const own = user.teacherCode;
  const linked = user.linkedTeacherCodes || [];

  if (own) {
    return (
      <Tooltip title="This user's own teacher code">
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{own}</Typography>
      </Tooltip>
    );
  }
  if (!linked.length) return <Typography variant="body2" color="text.disabled">—</Typography>;

  return (
    <Tooltip title={linked.length > 1 ? `Linked to: ${linked.join(', ')}` : 'Teacher this student is linked to'}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{linked[0]}</Typography>
        {linked.length > 1 && (
          <Chip size="small" label={`+${linked.length - 1}`} variant="outlined" />
        )}
      </Stack>
    </Tooltip>
  );
}

/**
 * Render the subscription badge. Students are NOT billable (0 of 976 carry
 * stripeInfo), so they read as n/a rather than "no plan" — otherwise the whole
 * student body would look like unconverted prospects.
 */
function SubscriptionCell({ subscription }) {
  const sub = subscription || {};
  if (!sub.applicable) {
    return (
      <Tooltip title="Billing does not apply to students">
        <Typography variant="body2" color="text.disabled">n/a</Typography>
      </Tooltip>
    );
  }
  if (!sub.tier) return <Chip size="small" label="No plan" variant="outlined" />;

  const label = sub.status
    ? `${TIER_LABEL[sub.tier] || sub.tier} · ${STATUS_LABEL[sub.status] || sub.status}`
    : (TIER_LABEL[sub.tier] || sub.tier);
  const chip = (
    <Chip
      size="small" label={label} color={TIER_COLOR[sub.tier] || 'default'}
      variant={sub.status === 'active' ? 'filled' : 'outlined'}
    />
  );
  return sub.tier === 'legacy'
    ? (
      <Tooltip title="Migrated from muzie.studio and not on a live plan. Whether they still pay on the legacy Stripe account cannot be seen from here.">
        <span>{chip}</span>
      </Tooltip>
    )
    : chip;
}

/**
 * Column registry. `fixed` columns are always shown and never offered in the
 * picker — the row would be unusable without a way to identify and open it.
 * `default: false` starts hidden.
 */
/**
 * `sortable` mirrors SORTABLE in routes/tenantUsers.js. The server ignores
 * anything not on its own allow-list, so a mismatch here degrades to the
 * default ordering rather than breaking — but they should agree.
 */
const FILTER_DEFAULTS = { status: '', plan: '', role: '', sort: 'created', dir: 'desc' };

const COLUMNS = [
  { key: 'name', label: 'Name', fixed: true, sortable: true },
  { key: 'email', label: 'Email', fixed: true, sortable: true },
  { key: 'role', label: 'Role', sortable: true },
  { key: 'teacherCode', label: 'Teacher code', sortable: true },
  { key: 'subscription', label: 'Subscription', sortable: true },
  { key: 'status', label: 'Status' },
  { key: 'platformRoles', label: 'Platform roles', default: false },
  { key: 'created', label: 'Created', sortable: true },
  { key: 'apiKey', label: 'API key', default: false },
  { key: 'actions', label: 'Open', fixed: true },
];

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function statusOf(user) {
  if (user.deleted) return { label: 'Deleted', color: 'error' };
  if (!user.active) return { label: 'Inactive', color: 'warning' };
  return { label: 'Active', color: 'success' };
}

function UserManagement() {
  const api = useApi();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage } = usePageSize('tenantUsers');
  const { isVisible, toggle, reset } = useTableColumns('tenantUsers', COLUMNS);
  const { filters, setFilter, setManyFilters, resetFilters } = useTableFilters('tenantUsers', FILTER_DEFAULTS);
  const [colMenu, setColMenu] = useState(null);
  // Search stays local: a persisted search box makes a filtered list look like
  // missing data next time you open the page.
  const [search, setSearch] = useState('');
  const { status, plan, role, sort, dir } = filters;
  const debouncedSearch = useDebouncedValue(search, 300);

  // A filter change invalidates the current page number.
  const changeFilter = (key, value) => { setFilter(key, value); setPage(0); };

  const onSort = (key) => {
    // ONE update: two setFilter calls would each spread the same stale
    // `filters`, so the second would silently discard the first.
    const nextDir = sort === key ? (dir === 'asc' ? 'desc' : 'asc') : 'asc';
    setManyFilters({ sort: key, dir: nextDir });
    setPage(0);
  };

  const { data, isLoading, isError, error: queryError, refetch, isFetching } = useQuery({
    queryKey: ['tenant-users', page, rowsPerPage, debouncedSearch, status, plan, role, sort, dir],
    queryFn: () => api.get('/api/tenant-users', {
      page: page + 1,
      limit: rowsPerPage,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(status && { status }),
      ...(plan && { plan }),
      ...(role && { role }),
      ...(sort && { sort }),
      ...(dir && { dir }),
    }),
    placeholderData: (prev) => prev,
  });

  const users = data?.users || [];
  const searchInfo = data?.search;
  const isSearching = Boolean(searchInfo);
  const countCapped = data?.pagination?.countCapped === true;
  const total = data?.pagination?.total ?? 0;
  const paginationCount = countCapped ? -1 : total;

  const open = (id) => navigate(`/users/${id}`);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Users</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every user under this tenant, including deactivated and deleted accounts.
        Double-click a row to open the full editor. Email addresses are immutable.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ flex: 1, minWidth: 220 }}
            helperText={
              searchInfo?.mode === 'prefix'
                ? 'Matches from the start of the email address'
                : ' '
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <TextField
            select size="small" label="Status" value={status}
            onChange={(e) => changeFilter('status', e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="deleted">Deleted</MenuItem>
          </TextField>
          <TextField
            select size="small" label="Role" value={role}
            onChange={(e) => changeFilter('role', e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All roles</MenuItem>
            <MenuItem value="teacher">Teacher</MenuItem>
            <MenuItem value="student">Student</MenuItem>
            <MenuItem value="studio">Studio</MenuItem>
          </TextField>
          <TextField
            select size="small" label="Subscription" value={plan}
            onChange={(e) => changeFilter('plan', e.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">All subscriptions</MenuItem>
            {PLAN_FILTERS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
          </TextField>
          <Tooltip title="Choose columns">
            <IconButton aria-label="Choose columns" onClick={(e) => setColMenu(e.currentTarget)}>
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={colMenu} open={Boolean(colMenu)} onClose={() => setColMenu(null)}>
            {COLUMNS.filter((c) => !c.fixed).map((c) => (
              <MenuItem key={c.key} dense onClick={() => toggle(c.key)}>
                <FormControlLabel
                  sx={{ pointerEvents: 'none' }}
                  control={<Checkbox size="small" checked={isVisible(c.key)} />}
                  label={c.label}
                />
              </MenuItem>
            ))}
            <Divider />
            <MenuItem dense onClick={() => { reset(); resetFilters(); setPage(0); setColMenu(null); }}>
              <Button size="small">Reset columns, filters &amp; sort</Button>
            </MenuItem>
          </Menu>
          <Tooltip title="Refresh">
            {/* span wrapper: a disabled button fires no events, so Tooltip has
                nothing to listen to without it. */}
            <span>
              <IconButton onClick={() => refetch()} disabled={isFetching}><RefreshIcon /></IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {queryError?.message || 'Failed to load users'}
        </Alert>
      )}

      {isSearching && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Search results are ordered by relevance — column sorting does not apply.
        </Typography>
      )}

      {searchInfo?.capped && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the closest {searchInfo.limit} matches. Refine your search to narrow it down.
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.filter((c) => isVisible(c.key)).map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.key === 'actions' ? 'right' : 'left'}
                    sortDirection={c.sortable && !isSearching && sort === c.key ? dir : false}
                  >
                    {c.sortable && !isSearching
                      ? (
                        <TableSortLabel
                          active={sort === c.key}
                          direction={sort === c.key ? dir : 'asc'}
                          onClick={() => onSort(c.key)}
                        >
                          {c.label}
                        </TableSortLabel>
                      )
                      : c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.filter((c) => isVisible(c.key)).length} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.filter((c) => isVisible(c.key)).length} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : users.map((user) => {
                const chip = statusOf(user);
                return (
                  <TableRow
                    key={user._id}
                    hover
                    onDoubleClick={() => open(user._id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {isVisible('name') && <TableCell>{user.name || '—'}</TableCell>}
                    {isVisible('email') && <TableCell>{user.email}</TableCell>}
                    {isVisible('role') && (
                      <TableCell>
                        {user.role
                          ? (
                            <Chip
                              size="small" label={user.role}
                              color={ROLE_COLOR[user.role] || 'default'}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          )
                          : <Typography variant="body2" color="text.secondary">—</Typography>}
                      </TableCell>
                    )}
                    {isVisible('teacherCode') && <TableCell><TeacherCodeCell user={user} /></TableCell>}
                    {isVisible('subscription') && (
                      <TableCell><SubscriptionCell subscription={user.subscription} /></TableCell>
                    )}
                    {isVisible('status') && (
                      <TableCell>
                        <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                      </TableCell>
                    )}
                    {isVisible('platformRoles') && (
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(user.roles || []).length
                            ? user.roles.map((r) => <Chip key={r} size="small" label={r} />)
                            : <Typography variant="body2" color="text.secondary">—</Typography>}
                        </Stack>
                      </TableCell>
                    )}
                    {isVisible('created') && (
                      <TableCell>
                        {user.created ? dayjs(user.created).format('DD MMM YYYY') : '—'}
                      </TableCell>
                    )}
                    {isVisible('apiKey') && (
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {user.apiKey ? `${user.apiKey.slice(0, 12)}…` : '—'}
                      </TableCell>
                    )}
                    {isVisible('actions') && (
                      <TableCell align="right">
                        <Tooltip title="Open user">
                          <IconButton size="small" onClick={() => open(user._id)}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Search results are a capped relevance ranking, not a paged set —
            showing a pager over them would imply pages that don't exist. */}
        {isSearching ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {users.length} {users.length === 1 ? 'match' : 'matches'}
            </Typography>
          </Box>
        ) : (
          <TablePagination
            component="div"
            count={paginationCount}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelDisplayedRows={({ from, to }) => (
              countCapped ? `${from}–${to} of 10,000+` : `${from}–${to} of ${total}`
            )}
          />
        )}
      </Paper>
    </Box>
  );
}

export default UserManagement;
